import { z } from "zod";

const urlMessage = (name: string) => `${name} must be an URL`;

/**
 * Specify "server side only" variables schema here.
 */
const serverSchema = z.object({});

/**
 * Specify client side variables schema here.
 */
const clientSchema = z.object({
  NODE_ENV: z.enum(["test", "development", "production"]),
  NEXT_PUBLIC_STAGE: z.enum(["local", "test", "dev", "stg", "pro"]),
  NEXT_PUBLIC_PROJECT_VERSION: z.string().optional(),
  NEXT_PUBLIC_FEDERATION_URL: z.string().url(urlMessage("NEXT_PUBLIC_FEDERATION_URL")),
  NEXT_PUBLIC_HASURA_URL: z.string().url(urlMessage("NEXT_PUBLIC_HASURA_URL")),
  NEXT_PUBLIC_GTM_IDS: z.string().transform((s) => s.split(",")),
  NEXT_PUBLIC_HOTJAR_ID: z.string().transform((s) => Number(s)),
  NEXT_PUBLIC_BASE_URL: z.string().url("NEXT_PUBLIC_BASE_URL"),
  NEXT_PUBLIC_ELECTRONIC_SIGNATURE_URL: z
    .string()
    .transform((s) =>
      s
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "")
    )
    .pipe(z.array(z.string().url(urlMessage("NEXT_PUBLIC_ELECTRONIC_SIGNATURE_URL")))),
  NEXT_PUBLIC_LOGIN_URL: z.string().url("NEXT_PUBLIC_LOGIN_URL"),
  NEXT_PUBLIC_HCAPTCHA_SITEKEY: z.string(),
  NEXT_PUBLIC_ACCOUNT_DATADOG_TOKEN: z.string(),
  NEXT_PUBLIC_IMAGES_URL: z.string().url(urlMessage("NEXT_PUBLIC_IMAGES_URL")),
  NEXT_PUBLIC_DS_IMAGES: z.string().url(urlMessage("NEXT_PUBLIC_DS_IMAGES")),
  NEXT_PUBLIC_CHATBOT_ENABLED: z.enum(["true", "false"]).optional(),
  NEXT_PUBLIC_PENDO_APP_ID: z.string(),
});

/**
 * Manually destruct server envs in this object as`process.env` is not a regular JS object.
 */
const serverEnv = {};

/**
 * Manually destruct client envs in this object as`process.env` is not a regular JS object.
 */
const clientEnv = {
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_STAGE: process.env.NEXT_PUBLIC_STAGE,
  NEXT_PUBLIC_PROJECT_VERSION: process.env.NEXT_PUBLIC_PROJECT_VERSION,
  NEXT_PUBLIC_FEDERATION_URL: process.env.NEXT_PUBLIC_FEDERATION_URL,
  NEXT_PUBLIC_HASURA_URL: process.env.NEXT_PUBLIC_HASURA_URL,
  NEXT_PUBLIC_GTM_IDS: process.env.NEXT_PUBLIC_GTM_IDS,
  NEXT_PUBLIC_HOTJAR_ID: process.env.NEXT_PUBLIC_HOTJAR_ID,
  NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
  NEXT_PUBLIC_ELECTRONIC_SIGNATURE_URL: process.env.NEXT_PUBLIC_ELECTRONIC_SIGNATURE_URL,
  NEXT_PUBLIC_LOGIN_URL: process.env.NEXT_PUBLIC_LOGIN_URL,
  NEXT_PUBLIC_HCAPTCHA_SITEKEY: process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY,
  NEXT_PUBLIC_ACCOUNT_DATADOG_TOKEN: process.env.NEXT_PUBLIC_ACCOUNT_DATADOG_TOKEN,
  NEXT_PUBLIC_IMAGES_URL: process.env.NEXT_PUBLIC_IMAGES_URL,
  NEXT_PUBLIC_DS_IMAGES: process.env.NEXT_PUBLIC_DS_IMAGES,
  NEXT_PUBLIC_CHATBOT_ENABLED: process.env.NEXT_PUBLIC_CHATBOT_ENABLED,
  NEXT_PUBLIC_PENDO_APP_ID: process.env.NEXT_PUBLIC_PENDO_APP_ID,
};

const mergedEnv = {
  ...clientEnv,
  ...serverEnv,
};

const isServer = typeof window === "undefined";
const isTestEnv = clientEnv.NODE_ENV === "test";
const mergedSchema = serverSchema.merge(clientSchema);

type EnvT = z.infer<typeof mergedSchema>;
type EnvKeyT = keyof EnvT;

const parsed = isServer || isTestEnv ? mergedSchema.safeParse(mergedEnv) : clientSchema.safeParse(mergedEnv);

if (parsed.success === false) {
  // eslint-disable-next-line no-console
  console.error("❌ Invalid environment variables:\n", parsed.error.flatten().fieldErrors);
  throw new Error("Invalid environment variables");
}

export const env = new Proxy(parsed.data as EnvT, {
  get(target, prop: EnvKeyT) {
    if (typeof prop !== "string") return undefined;

    if (!isServer && !isTestEnv && Object.keys(serverEnv).includes(prop)) {
      throw new Error("❌ Can't access server env on the client");
    }

    return target[prop];
  },
});
